import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import _ from 'lodash';
import {
  Text,
  ContactPhone,
  BootstrapDropdown as Dropdown,
  Header,
  SubHeader,
  SiteLogo,
  ContactEmail,
} from '@tradingblock/components';
import { ApplicationStep, AllApplicationSteps } from '../types';
import { getApplicationStepUrl, isApplicationStepInvalid } from '../services';
import { useApplication } from '../context';
import { useData } from '../state/selectors';
import { ApplicationType } from '@tradingblock/types';

const PageHeaderComponent: React.FC<RouteComponentProps> = ({ history }) => {
  const application = useData(d => d.data.application);
  const step = useData(d => d.ui.step);
  const siteGrp = process.env.REACT_APP_TB_SITE_GROUP;
  const clearer = useData(d => d.ui.clearer);
  const [{ navMenuOpen, accountType }, { setNavMenuOpen }] = useApplication();
  const applicationSteps = useMemo(
    () => (accountType === ApplicationType.Entity ? AllApplicationSteps.slice(0, 3) : AllApplicationSteps),
    [accountType]
  );
  const headerRef = useRef<HTMLDivElement>(null);

  const url = history.location.pathname;

  // Dynamic header height calculation
  useEffect(() => {
    const updateHeaderHeight = () => {
      try {
        if (headerRef.current) {
          const headerHeight = headerRef.current.offsetHeight;
          document.documentElement.style.setProperty('--header-actual-height', `${headerHeight}px`);

          // Update main padding to match actual header height
          const mainElement = document.querySelector('main');
          if (mainElement) {
            mainElement.style.paddingTop = `${headerHeight}px`;
            // Ensure consistent bottom padding
            mainElement.style.paddingBottom = 'var(--gap)';
          }
        }
      } catch (error) {
        console.warn('Failed to update header height:', error);
        // Fallback to CSS variables if JavaScript fails
        const mainElement = document.querySelector('main');
        if (mainElement) {
          mainElement.style.paddingTop = '';
          mainElement.style.paddingBottom = '';
        }
      }
    };

    // Update on mount and window resize
    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);

    // Update after a short delay to ensure all content is rendered
    const timeoutId = setTimeout(updateHeaderHeight, 100);

    // Additional update after navigation changes
    const intervalId = setInterval(updateHeaderHeight, 1000);

    return () => {
      window.removeEventListener('resize', updateHeaderHeight);
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [navMenuOpen]); // Recalculate when nav menu state changes

  const showNavigation = useMemo(() => {
    // if already created account, always hide nav
    if (application && application.accountId) {
      return false;
    }
    // show nav when on a application page, and have application data loaded or not on customer service for new accts
    return _.startsWith(url, '/apply/') && (application || !_.endsWith(url, '/customer-service'));
  }, [url, application]);

  const currentStep = useMemo(() => (!_.endsWith(url, '/summary') ? step : undefined), [url, step]);
  const latestStep = useMemo(() => application && application.latestStepSaved, [application]);

  const canEdit = useCallback(
    (step: ApplicationStep) => {
      const stepIndex = _.indexOf(applicationSteps, step);
      const latestStepIndex = latestStep && _.indexOf(applicationSteps, latestStep);
      return (
        (stepIndex === 0 || (!!latestStepIndex && latestStepIndex >= stepIndex)) &&
        !isApplicationStepInvalid(step, application, false, siteGrp, clearer)
      );
    },
    [latestStep, application, applicationSteps]
  );

  const onMenuToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    setNavMenuOpen(!navMenuOpen);
  };

  const onMenuClose = () => {
    setNavMenuOpen(false);
  };

  const onMenuKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setNavMenuOpen(false);
    }
  };

  return (
    <Header ref={headerRef}>
      <SubHeader onClick={onMenuClose}>
        <SiteLogo siteGroup={siteGrp} />
        <nav className="nav-utilities">
          <ul>
            <li>
              <Dropdown id="assistance" toggle={<Text id="contact" />}>
                <div className="dropdown-body">
                  <strong>
                    <Text id="contact.content" />
                  </strong>
                  <br />
                  <Text
                    id="contact.content"
                    textKey="description"
                    siteGroup={siteGrp}
                    data={{
                      email: <ContactEmail siteGroup={siteGrp} />,
                      phone: <ContactPhone siteGroup={siteGrp} />,
                    }}
                  />
                </div>
              </Dropdown>
            </li>
          </ul>
        </nav>
      </SubHeader>

      {showNavigation && (
        <nav
          className={`nav ${navMenuOpen ? 'nav-menu-open' : ''}`}
          role="navigation"
          aria-label="Application progress"
        >
          <div className="content">
            <button
              type="button"
              aria-haspopup="true"
              aria-expanded={navMenuOpen}
              aria-controls="nav-menu"
              className="nav-toggle"
              id="nav-toggle"
              onClick={onMenuToggle}
              onKeyDown={onMenuKeyDown}
              aria-label={navMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            >
              <i className="fal fa-bars" aria-hidden="true" />
            </button>
            <ul aria-labelledby="nav-toggle" className="nav-labels" id="nav-menu" role="menubar">
              {applicationSteps.map(s => (
                <NavigationStep
                  key={`step-${s}`}
                  step={s}
                  current={currentStep}
                  canEdit={canEdit(s)}
                  showLabel={true}
                  onClickLabel={() => setNavMenuOpen(false)}
                  applicationSteps={applicationSteps}
                />
              ))}
            </ul>
            <ul
              className={`nav-indicators ${accountType ? `nav-indicators-${accountType.toLowerCase()}` : ''}`}
              aria-hidden="true"
              role="presentation"
            >
              {applicationSteps.map(s => (
                <NavigationStep
                  key={`step-${s}`}
                  step={s}
                  current={currentStep}
                  canEdit={canEdit(s)}
                  applicationSteps={applicationSteps}
                />
              ))}
            </ul>
          </div>
        </nav>
      )}
    </Header>
  );
};

export const PageHeader = withRouter(PageHeaderComponent);

interface NavigationStepProps {
  step: ApplicationStep;
  current?: ApplicationStep;
  canEdit?: boolean;
  showLabel?: boolean;
  onClickLabel?: () => void;
  applicationSteps: ApplicationStep[];
}

const NavigationStep: React.FC<NavigationStepProps> = ({
  step,
  current,
  canEdit,
  showLabel,
  onClickLabel,
  applicationSteps,
}) => {
  const stepIndex = useMemo(() => _.indexOf(applicationSteps, step), [step]);
  const currentIndex = useMemo(() => _.indexOf(applicationSteps, current), [current]);
  const isCurrent = useMemo(() => stepIndex > -1 && stepIndex === currentIndex, [stepIndex, currentIndex]);

  const className = useMemo(() => {
    if (isCurrent) {
      return 'nav-step nav-present';
    }
    if (canEdit) {
      return 'nav-step nav-past';
    }
    return 'nav-step nav-future';
  }, [isCurrent, canEdit]);

  const label = useMemo(() => {
    if (!showLabel) {
      return '';
    }
    const text = <Text id={`header.nav.${step}`} />;
    if (!isCurrent && canEdit) {
      return <Link to={getApplicationStepUrl(step)}>{text}</Link>;
    }
    return text;
  }, [step, showLabel, isCurrent, canEdit]);

  return (
    <li className={className} onClick={onClickLabel} role="menuitem" tabIndex={0}>
      {label}
    </li>
  );
};
